import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { splitByCompanyGroup } from '@/lib/company-group';
import { cx } from '@/lib/cx';
import { currentCycle } from '@/lib/document-naming';
import { NAV_SURFACE_MODULE } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useProjectOptions } from '@/lib/use-project-options';
import {
  CAPACITY_CATEGORIES,
  CAPACITY_STATE_BADGE,
  capacityState,
  categoryLine,
} from '@/lib/project-capacity';
/*
 * ⚠️ **`EventCapacityAxisCity` IS DELIBERATELY NOT IMPORTED.** It describes
 * `data.cities`, the global column axis, and this panel stopped laying out from
 * it on 2026-09-13 — see the header. The type is not wrong and is not withdrawn;
 * it simply has no reader here, and importing it "for completeness" would put the
 * global scale back within reach of the next person adding a column header.
 *
 * ⚠️ **`EventCapacityProjectCity` extends it field for field**, so the two are
 * interchangeable to `tsc` and are NOT interchangeable in meaning: this one's
 * `companyCount` is *on this project*, the axis one's is *distinct across the
 * grid*. Every count this file renders comes from the project one.
 */
import type {
  EventCapacityCompany,
  EventCapacityCompanyCity,
  EventCapacityProject,
  EventCapacityProjectCity,
  EventCapacityResponse,
} from '@/lib/types';
import {
  Badge,
  Button,
  Combobox,
  EmptyState,
  Field,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';

/*
 * PROJECT CAPACITY — a cross-tab of CITIES across, COMPANIES down, banded by
 * PROJECT. The second tab on `/client-deliverables`, owner's decision
 * 2026-09-13.
 *
 * ⚠️ **THE LABEL WAS RENAMED ON 2026-09-13.** *SUPERSEDED, the screen's name:
 * "Event Capacity".* **The rename is WORDING ONLY** — this file, its exported
 * symbols, the `EventCapacity*` types, `api.eventCapacity`, the
 * `/event-capacity` endpoint and the `?deliverables=capacity` URL value are all
 * untouched and must stay that way. A grep for `EventCapacity` therefore still
 * returns plenty, and every hit is a key rather than a label.
 *
 * It answers one question the tracker beside it cannot: *who is confirmed for
 * which city on which project*. The tracker is a list of line items; this is
 * their intersection, which is a shape a list cannot show.
 *
 * ── 🔴 THE AXIS IS PER BAND, AND ONE TABLE PER BAND IS WHAT MAKES IT POSSIBLE
 *
 * **Owner's instruction, 2026-09-13: each project band shows ONLY that project's
 * cities**, so scrolling to the next project shows the next project's cities and
 * nothing else. ⚠️ *SUPERSEDED, and it was the whole layout decision until that
 * day: "**`data.cities` — the union across the returned projects … IS the column
 * axis.** The header row, the table floor, every `colSpan` … all derive from it
 * and from nothing else. ⚠️ **`project.cities` IS APPLICABILITY, NOT LAYOUT** …
 * laying out from `project.cities` gives a different header per band — several
 * stacked tables, which is not a cross-tab."*
 *
 * **Several stacked tables is now exactly what this is**, and the sentence that
 * argued against it was right about the mechanism: a band's columns are
 * `project.cities`, so two bands legitimately have different column COUNTS, and
 * ⛔ **one `<table>` cannot express that.** A single table's columns are shared
 * by every `<tbody>` in it, so a 3-city band inside a 9-column table either
 * trails six empty cells or spans them — which is the global axis again, wearing
 * a different name. **So each band is its own `<Table>` in its own `TableWrap`**,
 * with its own header, its own floor and its own horizontal scroller.
 *
 * ⚠️ **`data.cities` IS NO LONGER READ BY THIS PANEL AT ALL.** It is still on the
 * wire and `EventCapacityAxisCity` still describes it; nothing here imports
 * either. See the ruling on the counts below for why the last reader went.
 *
 * ── ⛔ MATCH A CELL TO A CITY BY ID. NEVER BY POSITION ──────────────────────
 *
 * **This is the one thing in this file that fails silently and expensively, and
 * per-band columns do NOT retire it.** `company.cities` holds ONLY the cells
 * that tick — no entry per column, no `false` — so its length is not the band's
 * column count and its order is not the band's column order. A positional zip
 * **looks perfectly correct on a single-band grid and misfiles every tick on the
 * second**, because a misplaced tick is indistinguishable from a real one: there
 * is no blank cell to notice, no error, and nothing for `tsc` to see.
 *
 * ✅ **`ticksByCityId` is the only way a tick is looked up here**, and it is keyed
 * by `cityId`. **`bandColumns` never produces an index anybody could zip against**
 * — it hands back city OBJECTS, each carrying its own `id` and its own figure, so
 * there is no parallel array in this file for the two to fall out of step.
 *
 * ── ✅ A COUNTED ZERO NO LONGER NEEDS TELLING APART FROM A BLANK ────────────
 *
 * 🔴 **THE DISTINCTION IS CARRIED BY THE CITY SIMPLY BEING ABSENT FROM THE BAND.**
 * ⚠️ *SUPERSEDED: three cell states, of which "**either row, project does NOT run
 * there — blank, not applicable**" was the one "easy to lose", told apart by an
 * `applicableCityIds` set.* **That state is now unreachable by construction**: a
 * band's columns ARE the cities its project runs in, so there is no cell to draw
 * for a city it does not. What survives is the half that always mattered — **a
 * `0` in a band header is a counted zero** (*this project runs here and nobody is
 * confirmed*), and the old confident-wrong-answer it guarded against — a `0`
 * under a city a project never visits — cannot be printed because that column
 * does not exist.
 *
 * ⚠️ **So `applicableCityIds` and `countsByCityId` are DELETED rather than kept
 * and unused.** A set that no longer narrows anything is a guard a reader will
 * trust; and a `counts.get(city.id)` lookup over a list built from the same
 * `project.cities` can never miss, which makes the `?? 0` this file spent two
 * comments forbidding look harmless to whoever writes it next.
 *
 * ── 🔴 THE COUNTS ARE PER BAND, AND THE GLOBAL ROW IS GONE ──────────────────
 *
 * **Owner's decision, 2026-09-13: every number describes the band it sits in.**
 * ⚠️ *SUPERSEDED: "🔴 **THE HEADER COUNTS DISTINCT COMPANIES ACROSS THE GRID; A
 * BAND ROW COUNTS COMPANIES ON THAT PROJECT** … the bands add up to MORE than the
 * header. ⛔ **Owner's ruling: no totals row, no column footer, no grand total
 * per city, and NO SENTENCE EXPLAINING THE GAP.**"*
 *
 * **A global figure has no column to sit under once the axis is per band**, so
 * the all-projects summary — the `Companies by city` roster reading *"AHMEDABAD
 * 23, MUMBAI 47 …"* — is removed outright rather than moved somewhere it would
 * be read as a band's own.
 *
 * ✅ **THE COUNTS ARE KEPT, AND THEY MOVED INTO THE BAND'S OWN COLUMN HEADER** —
 * `project.cities[].companyCount`, companies confirmed for that city **on this
 * project**. ⛔ **The separate `Companies confirmed` count row is gone with it**:
 * the figure now sits in the header cell of the column it counts, which is one
 * row less chrome per band and puts the number where the reader is already
 * looking.
 *
 * ✅ **AND THE FIGURE IS NOW VERIFIABLE BY EYE, WHICH THE OLD ONE NEVER WAS.**
 * A band header's count is the number of ticks in the column beneath it, so the
 * two-counts-that-cannot-be-reconciled problem does not survive this change —
 * there is no second scale on screen to disagree with. ⚠️ **The one figure that
 * is still NOT reconcilable is `companiesOffAxis`**, which counts companies with
 * a real tick and no drawable cell; it stays on the band heading, in words, and
 * is never summed into anything. See `OffAxisNote`.
 *
 * ── THE BAND HEADING IS OUTSIDE THE SCROLLER, AND NOT VERTICALLY STICKY ────
 *
 * ✅ **IT SITS OUTSIDE THE `TableWrap`, WHICH RETIRES A HACK.** *SUPERSEDED: a
 * `colSpan` heading row inside the table carrying a `sticky left-3` span, because
 * "a spanning cell has no edge to freeze to".* A heading outside the horizontal
 * scroller cannot scroll out of view sideways at all, so the project's name is
 * always on screen while the reader drags the grid across — the thing that
 * `sticky left-3` was approximating.
 *
 * ⛔ **IT IS DELIBERATELY NOT `sticky top-0`, AND THAT IS NOT AN OVERSIGHT.**
 * `AppShell` does not scroll `<main>` — **the WINDOW scrolls** — and the top bar
 * is `sticky top-0 z-30`. So a page-content `sticky top-0` pins *behind* the bar:
 * present, correctly positioned, carrying a z-index it is entitled to, and
 * invisible. ⚠️ **And the offset that would clear it cannot be written down**:
 * the bar's height varies with the preview and support-session banners stacked
 * inside it, so `top-14` is right on some sessions and wrong on others. The two
 * pages here that do stick a header (`UniversityPartnershipsPage`,
 * `SalesTrackerBoard`) both sit inside a container with a BOUNDED HEIGHT that
 * owns its own scrolling; this grid does not, and giving every band its own
 * nested vertical scrollbar to buy a sticky header is a worse trade.
 *
 * ✅ **What replaces it is the per-band header itself.** A global axis left a
 * reader 200 rows deep with no header anywhere; repeating the axis per band means
 * the columns are named again a few rows later, every band, at every width.
 *
 * ⚠️ **EACH `TableWrap` CARRIES `isolate`, AND IT IS NOT FIXING A COLLISION THAT
 * EXISTS TODAY.** Nothing here is positioned outside a table, so today's headings
 * cannot tie with anything. What changed is arithmetic: one table became N, so
 * there are now N sets of `z-20`/`z-10` frozen cells sticky in the page's own root
 * stacking context instead of one. A context per band keeps each band's numbers
 * ordering against each other and against nothing else, and it means a sticky
 * heading — if the offset problem above is ever solved — is a local change rather
 * than a move on the ladder. ⛔ **A bigger number is the wrong fix in either
 * case**: it buys one collision and spends a rung the ladder reserves
 * (`src/index.css` — nothing in a page may go above 20).
 *
 * ── THE LOADER IS A PROP, AND THE PREDICTION IT CARRIED CAME TRUE ──────────
 *
 * ✅ **`api.eventCapacity` EXISTS AND `ClientDeliverablesPage` PASSES IT**, so
 * the not-wired notice below is now UNREACHABLE. *SUPERSEDED: "`GET
 * /api/v1/event-capacity` IS WRITTEN AND NOT DEPLOYED, AND `src/lib/api.ts` HAS
 * NO METHOD FOR IT … there is no honest way for this file to make the request
 * itself."*
 *
 * ⚠️ **The prop stays OPTIONAL and the notice stays written**, because being
 * structural is what made that transition cost one line. A boolean would have
 * had to be found and unset, and nothing would have failed if it were not: the
 * branch is dead by SHAPE, which is the only kind of dead branch that cannot
 * come back to life by accident.
 *
 * ── THE CYCLE IS RESOLVED HERE, BECAUSE THE SERVER REFUSES TO GUESS ────────
 *
 * 🔴 **`cycleId` IS REQUIRED. OMITTING IT IS A 422 NAMING IT, NEVER A
 * CURRENT-CYCLE GRID**, and the server is RIGHT to refuse: deriving *the cycle
 * whose window contains today* server-side would put a SECOND copy of a rule
 * this app already owns in `currentCycle()` — the same rule that names a new
 * deal or quote and decides what `/projects` opens on. The two would have agreed
 * the day they were written and drifted at a financial-year boundary, and that
 * boundary MOVED on 2026-08-31 (June–May to April–March). A grid quietly showing
 * last year's cycle is a screen full of real figures under the wrong heading.
 *
 * ⚠️ **So "no cycle contains today" is an ORDINARY STATE with its own branch** —
 * not an error, not an empty grid, and not a request. See `noCycle`.
 */

/* -------------------------------------------------------------------------
 * The wire
 * --------------------------------------------------------------------- */

/**
 * What the panel needs done for it — satisfied by `api.eventCapacity`.
 *
 * ⛔ **THE WIRE TYPES LIVE IN `src/lib/types.ts` AND ARE IMPORTED, NOT RE-DECLARED
 * HERE.** *SUPERSEDED: "THESE BELONG IN `src/lib/types.ts` AND ARE NOT THERE
 * YET … until then this file is the one copy."* They are there; the local set is
 * gone rather than re-exported, so there is exactly one import path for each and
 * no second place for a field to be added to.
 *
 * ⛔ **The endpoint is UNPAGED and sends no `meta`**, so nothing downstream may
 * read `meta.totalPages` — it is `undefined`, and a pager built on it renders
 * "Page 1 of NaN" rather than failing.
 *
 * 🔴 **`cycleId` IS `number`, NOT `number | null`, AND THAT NARROWING IS THE
 * GUARD.** *SUPERSEDED: "`cycleId: null` means **omit the key** and let the
 * server answer for whatever it considers current."* **The server refuses
 * that** — `cycleId` is not optional on its schema, so omitting it is a **422
 * naming `cycleId`**, never a current-cycle grid. Under `strictFunctionTypes` a
 * loader promising to accept `null` is not satisfiable by a method that accepts
 * only `number`, so **`tsc` names the MOUNT** rather than letting that 422 ship.
 * Resolving the cycle is the caller's job — see this file's header, and
 * `resolvedCycleId`.
 *
 * ⚠️ **An empty `projectIds` OMITS `projectId`** — every project. `?projectId=`
 * is a 422 on a `.strict()` schema, not a request for all of them.
 */
export type EventCapacityLoader = (
  params: { cycleId: number; projectIds: readonly number[] },
  signal: AbortSignal,
) => Promise<EventCapacityResponse>;

/* -------------------------------------------------------------------------
 * The URL keys
 * --------------------------------------------------------------------- */

/**
 * 🔴 **A DISTINCT KEY, NOT `?tab=`.** `/client-deliverables` is mounted as a
 * child of `/operations`, which owns `?tab=` and clears a fixed key list when a
 * pill changes. Sharing the key would make one control answer two questions.
 */
export const DELIVERABLES_TAB_PARAM = 'deliverables';

/** The only value. The tracker is spelled by the key's ABSENCE. */
export const CAPACITY_TAB_VALUE = 'capacity';

/**
 * 🔴 **SEPARATE FROM THE TRACKER'S OWN `cycleId` AND `projectId`, DELIBERATELY.**
 *
 * Two reasons, and each alone is enough:
 *
 * - **`projectId` on the tracker is SINGLE-VALUED and is read as
 *   `Number(filters.projectId)`.** This grid's project filter is a CSV, so a
 *   shared key would leave `projectId=41,52` behind for the tracker, which sends
 *   `Number('41,52')` — `NaN` — and collects a 422 on its only list request.
 * - **The tracker's `cycleId` is a SAVED filter**, stored against the account. A
 *   cycle picked here would narrow the tracker on the reader's next visit and
 *   persist that, which is the cross-contamination `CLEARED_ON_SWITCH` exists to
 *   stop, one level down.
 */
export const CAPACITY_CYCLE_PARAM = 'capacityCycleId';
export const CAPACITY_PROJECT_PARAM = 'capacityProjectId';

/**
 * Every query key this screen owns.
 *
 * ⛔ **`OperationsDashboardPage` SPREADS THIS INTO `CLEARED_ON_SWITCH`** rather
 * than repeating the three strings, so the two cannot drift. That file's own
 * header says nothing there can notice a page gaining a key — an import is the
 * only version of that which cannot be forgotten.
 */
export const EVENT_CAPACITY_QUERY_KEYS = [
  DELIVERABLES_TAB_PARAM,
  CAPACITY_CYCLE_PARAM,
  CAPACITY_PROJECT_PARAM,
] as const;

/* -------------------------------------------------------------------------
 * The arithmetic
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **THE `px` NUMBERS AND THE CLASS STRINGS MUST AGREE.** The classes are what
 * the browser reads; the numbers are what `tableMinWidthPx` sums. They sit
 * beside each other for that reason — a class changed without its number puts
 * the table's floor below the sum of its columns, at which point the browser
 * shrinks every column proportionally and the truncation comes straight back.
 *
 * The floor for each is the HEADER first: a column header is `text-2xs`
 * uppercase with `tracking-[0.06em]` plus `TH`'s `px-3` (24px), so a city name
 * is what sets the city column and not the single digit under it.
 */
/*
 * 🔴 **WIDENED 260 → 360 ON 2026-09-13, owner's report that real names truncate.**
 * `Arizona State University - W. P. Carey School of Business` is 56 characters;
 * at `text-sm` `font-medium` that is roughly 380px of text, so **360 does not end
 * truncation and is not meant to** — every pixel here is taken from the city
 * columns, and a company column wide enough for the longest name in production
 * would be half the visible table at 1280. What it does is carry the ~53
 * characters that discriminate two schools of one university, which is the job
 * this column has: `HSNC University - Thadomal College of Engineering` and `HSNC
 * University - K C College` must not both read `HSNC University - …`.
 *
 * ⚠️ **THREE NUMBERS MOVE TOGETHER AND ONE OF THEM IS DERIVED.** The px, the
 * header's `w-`, and the body cells' `max-w-` are the three; the table's floor is
 * the fourth and is **computed from the px by `tableMinWidthPx`** rather than
 * written down, which is the only reason a width change here cannot leave the
 * floor behind. **Do not convert that sum back into a literal**: this repo
 * records the same three lists silently drifting apart on the quote and invoice
 * line grids, where the floor is a hand-written `min-w-[1492px]`.
 *
 * ⚠️ **A class changed without its number puts the floor BELOW the sum of the
 * columns**, at which point the browser shrinks every column proportionally and
 * the truncation this change exists to reduce comes straight back — with nothing
 * failing.
 *
 * ⚠️ **THERE IS NO `table-fixed` AND NO `<colgroup>` HERE, so there is no fourth
 * list to move.** The header cell requests the width and the body cells cap it;
 * `truncate` inside the cell is what makes the cap visible. Under `table-fixed` a
 * heading wider than its column PAINTS OVER ITS NEIGHBOUR instead of truncating,
 * which is why the city headings below carry `wrap` rather than relying on that.
 */
const COMPANY_PX = 360;
const COMPANY_W = 'w-[360px]';
const COMPANY_MAX_W = 'max-w-[360px]';

/*
 * ⚠️ **UNCHANGED AT 124, DELIBERATELY.** The floor for a city column is the
 * HEADER — `text-2xs` uppercase with `tracking-[0.06em]` plus `TH`'s `px-3` — and
 * not the tick or the one- or two-digit count under it. Narrowing these to pay
 * for the company column was considered and refused: it is a second change with
 * its own failure mode (a heading wrapping to three lines on one band and two on
 * the next, so no two bands' header rows are the same height), and **per-band
 * columns already bought most of the slack** — a band running four cities now
 * draws four columns rather than every city in the cycle.
 */
const CITY_PX = 124;
const CITY_W = 'w-[124px]';

/*
 * The company's RECORDED type, right after the company (owner 2026-10-18). Not
 * frozen: a second frozen column needs offset arithmetic this file does not have.
 * Drawn only when the response carries groups — an older API renders as before.
 */
const TYPE_PX = 140;
const TYPE_W = 'w-[140px]';
const TYPE_MAX_W = 'max-w-[140px]';

/**
 * The table's floor.
 *
 * ⛔ **AN INLINE `style`, NEVER A TAILWIND CLASS.** Tailwind v4 scans SOURCE
 * TEXT, so `min-w-[${n}px]` emits no rule at all — the class lands in the DOM
 * looking applied and does nothing, leaving the table with no floor and every
 * column squeezed. The city count is DATA, so there is no literal to write.
 * `Table` accepts `style` for exactly this.
 */
function tableMinWidthPx(cityCount: number, grouped: boolean): number {
  return COMPANY_PX + (grouped ? TYPE_PX : 0) + CITY_PX * cityCount;
}

/**
 * How many cells one body row holds, and therefore what a full-width row spans.
 *
 * ⚠️ **DERIVED, NEVER WRITTEN DOWN.** A band heading that spans the wrong width
 * does not throw — it draws a rule that stops short of the table, or pushes an
 * extra empty column onto every band. Both read as a styling accident rather
 * than as an arithmetic one, which is why the count has exactly one definition.
 */
function bodyColumnCount(cityCount: number, grouped: boolean): number {
  return 1 + (grouped ? 1 : 0) + cityCount;
}

/**
 * Whether the response carries company groups at all. `undefined` on every
 * company is an older API, which renders ungrouped and without the Type column.
 * Decided once for the whole grid so every band agrees.
 */
function responseIsGrouped(projects: readonly EventCapacityProject[]): boolean {
  return projects.some((project) => project.companies.some((c) => c.group !== undefined));
}

/** The company's recorded type, `—` when none is recorded. */
function companyTypeLabel(company: EventCapacityCompany): string {
  return company.type?.name ?? '—';
}

/* -------------------------------------------------------------------------
 * Band tone
 * --------------------------------------------------------------------- */

/**
 * 🔴 **A BAND'S TONE IS ITS INDEX INTO THIS ARRAY, MODULO ITS LENGTH — NEVER A
 * MAP KEYED ON THE PROJECT.** The bands are DATA: a project created tomorrow
 * must render without this file being edited, and a map would answer `undefined`
 * for it — an untinted band, which reads as a plain block rather than as a
 * missing case. The modulo wraps, and two bands sharing a tint is a legible page
 * because **the tint was never the thing carrying the meaning**: the project's
 * NAME identifies the band and the tint only groups the rows under it.
 *
 * ⚠️ *SUPERSEDED (2026-10 UI rework): "`chart-*`, NOT the status families."*
 * The band heading is a NEUTRAL wash now — one quiet `surface-sunken` step for
 * every band — on the rework's ruling that chart colours belong to charts. The
 * half that survives: a project is not a status, so it never takes a status
 * family either. The array and the modulo stay so a second tone can return
 * without re-deriving the one-place rule below.
 *
 * ⚠️ **THE TINT IS ON THE BAND HEADING ALONE, AND THE ROWS ARE PLAIN.** That is
 * not restraint for its own sake: `Table`'s frozen body cell bakes in
 * `bg-surface` because a sticky cell must be opaque, and a striped tint appended
 * through `className` would be a second `background-color` on the same element —
 * whichever Tailwind emitted last wins, which is not a thing to make the frozen
 * column's legibility depend on.
 */
const BAND_TONES: readonly string[] = ['bg-surface-sunken'];

const FIRST_BAND_TONE = 'bg-surface-sunken';

/**
 * The tone for the band at `index`.
 *
 * 🔴 **THE ONE PLACE THE MODULO IS WRITTEN.** The table and the sub-`lg` cards
 * both take their tone from here, so the two cannot answer differently for one
 * band — a reader crossing the breakpoint must not have to relearn which band is
 * which. The `??` is unreachable while the array has members and is written
 * rather than asserted away: an index signature says the lookup can miss, and a
 * `!` silencing it would go on being silent if the array were ever emptied.
 */
function bandTone(index: number): string {
  return BAND_TONES[index % BAND_TONES.length] ?? FIRST_BAND_TONE;
}

/* -------------------------------------------------------------------------
 * Reading a band
 * --------------------------------------------------------------------- */

/**
 * 🔴 **THIS BAND'S COLUMNS — the cities its own project runs in, and nothing
 * else.** Owner's instruction, 2026-09-13. Both views call this and neither
 * derives columns any other way, so the table and the cards cannot disagree about
 * which cities a project runs in.
 *
 * ⚠️ *SUPERSEDED: `applicableCityIds`, a `Set<number>` of the global axis columns
 * that applied to a band, and `countsByCityId`, a `cityId → count` map read
 * alongside it. Both are DELETED rather than kept:* a set that no longer narrows
 * anything reads as a guard, and a map lookup that can never miss makes the
 * `?? 0` this file forbids twice look harmless to whoever writes it next.
 *
 * ⛔ **IT RETURNS CITY OBJECTS, WHICH IS THE POINT.** Each one carries its own
 * `id` and its own `companyCount`, so a renderer walking this list never holds an
 * index into a second array — there is no parallel list here for a positional zip
 * to fall out of step with. The only lookup left in the file is by `city.id`.
 *
 * ⚠️ **SORTED HERE RATHER THAN TRUSTED FROM THE RESPONSE, and NOT filtered
 * against `data.cities`.** `sortOrder` is the cities master's own, so sorting on
 * it reproduces the order the old global axis had — two bands that both run
 * Mumbai and Delhi list them the same way round, which is what stops a reader
 * relearning the axis at every band. **Filtering the global axis by the band's
 * ids would have given that too, and would DROP a city the band runs in that the
 * axis somehow omits** — silent column loss, where a defensive sort can only ever
 * show one column too many.
 *
 * ⚠️ **A COPY, because `.sort()` mutates.** `project.cities` is response data
 * rendered by two views; sorting it in place would reorder what the other one
 * reads, on whichever happened to render first.
 */
function bandColumns(project: EventCapacityProject): EventCapacityProjectCity[] {
  return [...project.cities].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name) || a.id - b.id,
  );
}

/**
 * One company's ticks, indexed by city id.
 *
 * ⛔ **BY ID, NEVER BY POSITION — and per-band columns do NOT retire this.**
 * `company.cities` holds only the cells that tick, so its length is not the
 * band's column count and its order is not the band's column order: a company
 * confirmed for one city on a four-city band supplies a one-entry array. Zipping
 * that against the columns puts a real confirmation in the wrong city, and **it
 * looks perfectly correct on a single-band grid and misfiles everything on the
 * second** — a misplaced tick is indistinguishable from a real one, so there is
 * no blank cell to notice, nothing logged and nothing for `tsc` to see.
 */
function ticksByCityId(company: EventCapacityCompany): Map<number, EventCapacityCompanyCity> {
  const ticks = new Map<number, EventCapacityCompanyCity>();
  for (const entry of company.cities) ticks.set(entry.cityId, entry);
  return ticks;
}

/**
 * A PENDING row (owner 2026-10-27): a live deal line on this project and nothing
 * sold on it. `undefined` (older API) is sold. Pending rows never add to a count.
 */
function isPending(company: EventCapacityCompany): boolean {
  return company.pending === true;
}

/** Sold rows only — the figure a group run's label carries. */
function soldRowCount(companies: readonly EventCapacityCompany[]): number {
  return companies.filter((company) => !isPending(company)).length;
}

/** The city label a card or a `title` uses — the name with its country, where there is one. */
function cityLabelOf(city: EventCapacityProjectCity): string {
  return city.country ? `${city.name}, ${city.country.name}` : city.name;
}

/* -------------------------------------------------------------------------
 * A tick
 * --------------------------------------------------------------------- */

/**
 * How one confirmation was reached, in words.
 *
 * ⛔ **BOTH FLAGS ARE READ, AND THE OVERLAP HAS ITS OWN SENTENCE.** At least one
 * is always true, so the fourth branch is a contract violation rather than an
 * ordinary state — and it still renders a TICK, because dropping it would hide a
 * confirmation that exists. Fail towards the visible answer.
 */
function tickTitle(tick: EventCapacityCompanyCity, cityLabel: string): string {
  // PENDING (owner 2026-10-27): a live deal line, nothing sold on the project.
  if (tick.pending === true) {
    if (tick.viaAllCities) return `Pending for ${cityLabel} on an all-cities deal line`;
    return `Pending for ${cityLabel} on a deal line naming it`;
  }
  if (tick.viaExplicit && tick.viaAllCities) {
    return `Confirmed for ${cityLabel}, both on a line naming it and on an all-cities line`;
  }
  if (tick.viaAllCities) return `Confirmed for ${cityLabel} on an all-cities line`;
  if (tick.viaExplicit) return `Confirmed for ${cityLabel} on a line naming it`;
  return `Confirmed for ${cityLabel}`;
}

function CheckMark() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

/**
 * One confirmed cell.
 *
 * ⚠️ **THE ALL-CITIES MARKER IS NOT HELPER TEXT.** It is a fact about the data
 * the reader cannot otherwise see — a company that said *"every city"* and one
 * that chose this city are the same tick otherwise — which is the fourth of the
 * four kinds the no-helper-text prohibition keeps. A sweep must not take it, nor
 * the one-line legend under the grid that says what it means.
 *
 * ⚠️ **NOT COLOUR ALONE.** The marker is a glyph beside the check and a sentence
 * on the cell's `title`, and the legend repeats it in words.
 */
function TickCell({ tick, cityLabel }: { tick: EventCapacityCompanyCity; cityLabel: string }) {
  const title = tickTitle(tick, cityLabel);
  // A PENDING tick is the warning token (owner 2026-10-27); a sold tick stays `text-fg`.
  return (
    <span
      className={cx('inline-flex items-center gap-0.5', tick.pending === true ? 'text-warning' : 'text-fg')}
      title={title}
    >
      <CheckMark />
      {tick.viaAllCities && (
        <span aria-hidden="true" className="text-2xs leading-none text-fg-muted">
          *
        </span>
      )}
      <span className="sr-only">{title}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------
 * The company column
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **`shortName` IS A SECOND LINE, NEVER A SUBSTITUTE FOR `name`.** On a LIST
 * the rows ARE the discriminator: `HSNC University - Thadomal College of
 * Engineering` and `HSNC University - K C College` are two customers that bill
 * separately, and two rows both reading `HSNC` look like a duplicate rather than
 * like a truncation. `/companies` renders it the same way.
 */
function CompanyNameCell({ company }: { company: EventCapacityCompany }) {
  const shortName = company.shortName?.trim() ?? '';
  return (
    <span className="block min-w-0">
      <span className="flex min-w-0 items-center gap-1.5">
        <TextLink
          to={`/companies/${company.id}`}
          className="block min-w-0 truncate text-sm font-medium"
          title={company.name}
        >
          {company.name}
        </TextLink>
        {/* PENDING row (owner 2026-10-27): on a deal, nothing sold on this project. */}
        {isPending(company) && (
          <Badge tone="warning" variant="outline" className="shrink-0">
            Deal
          </Badge>
        )}
      </span>
      {shortName !== '' && shortName !== company.name && (
        <span className="block truncate text-2xs text-fg-muted">{shortName}</span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------
 * The band heading
 * --------------------------------------------------------------------- */

/** One fact on a band heading, with the category's state badge where it has one. */
interface BandFact {
  key: string;
  text: string;
  badge: { label: string; tone: 'success' | 'neutral' } | null;
  /** Set only on the pending fact, which takes the pending ticks' token. */
  pending?: true;
}

/**
 * What a band's heading says about itself.
 *
 * ✅ **SOLD AGAINST PLAN IS A FRACTION NOW — owner override 2026-10-27.**
 * *SUPERSEDED: "THE COMPANY COUNT AND THE CAPACITY ARE TWO FACTS, NEVER A
 * FRACTION … `3 of 12` invents a unit."* Planned capacity and target are now
 * set per category and measured in companies, and `sold` counts distinct
 * companies per category, so "Institutions 12 of 30 · target 20" compares like
 * with like. The wording lives in `lib/project-capacity.ts` (`categoryLine`);
 * do not re-inline it. `companyCount` (the rows on screen) stays its own fact and
 * is never set against a capacity: it counts confirmed attendance, not sold.
 *
 * ⚠️ **A category with no capacity, no target and nothing sold is not shown**,
 * so a project nobody planned reads as before rather than as "Partners 0".
 * `null` is never printed as `0`.
 *
 * ⚠️ **AN OLDER API (`capacityPlan` undefined) KEEPS THE OLD FACT** — "capacity
 * N", shown only when the project is capped.
 *
 * ✅ **THE CITY COUNT IS A THIRD FACT, ADDED 2026-09-13 WITH THE PER-BAND AXIS.**
 * Under a global axis every band had the same columns, so the number was a
 * property of the page and worth nothing on a heading; now it says **how wide
 * THIS band is** — whether there is anything to the right of what the reader can
 * see, before they try scrolling to find out.
 *
 * ⚠️ **IT IS THE COLUMN COUNT AND NOTHING ELSE, so it reconciles with nothing.**
 * It is not a share of anything, it does not sum with `companyCount`, and it must
 * never be set beside the capacity as though the two were comparable — a project
 * capped at 12 running in 4 cities is not "4 of 12".
 */
function bandFacts(project: EventCapacityProject): BandFact[] {
  const facts: BandFact[] = [
    {
      key: 'companies',
      text: `${project.companyCount} ${project.companyCount === 1 ? 'company' : 'companies'}`,
      badge: null,
    },
  ];
  // PENDING (owner 2026-10-27): its own fact, never added into the company count.
  const pendingCount = project.pendingCount ?? 0;
  if (pendingCount > 0) {
    facts.push({ key: 'pending', text: `${pendingCount} pending`, badge: null, pending: true });
  }
  const plan = project.capacityPlan;
  if (plan !== undefined) {
    const sold = project.sold ?? { institution: 0, partner: 0 };
    for (const { key } of CAPACITY_CATEGORIES) {
      const figures = plan[key];
      if (figures.capacity === null && figures.target === null && sold[key] === 0) continue;
      facts.push({
        key,
        text: categoryLine(key, sold, plan),
        badge: CAPACITY_STATE_BADGE[capacityState(sold[key], figures)],
      });
    }
  } else if (project.capacity !== null) {
    facts.push({ key: 'capacity', text: `capacity ${project.capacity}`, badge: null });
  }
  /*
   * Real cities only (owner 2026-10-18). A project with no cities arrives with
   * ONE synthetic "Virtual" column, which renders like any other column but is
   * not a city — so a Virtual band shows no city count rather than "1 city".
   */
  const cityCount = project.cities.filter(isRealCity).length;
  if (cityCount > 0) {
    facts.push({ key: 'cities', text: `${cityCount} ${cityCount === 1 ? 'city' : 'cities'}`, badge: null });
  }
  return facts;
}

/**
 * Whether a column is an `f_cities` row. The server flags its synthetic column
 * for a project with no cities (`isVirtual`, id `0`); the id test covers a
 * response without the flag, since real city ids are positive.
 */
function isRealCity(city: EventCapacityProjectCity): boolean {
  return city.isVirtual !== true && city.id > 0;
}

/**
 * The off-axis line.
 *
 * 🔴 **NEVER SUMMED INTO THE HEADER COUNT.** It is normally `0`, so this branch
 * will almost never be exercised — it is written anyway, and it is UNDRIVEN.
 *
 * ⚠️ **A fact about the data the reader cannot otherwise see**, which is why it
 * survives the no-helper-text rule: without it a company with a real,
 * confirmed attendance is simply absent from the grid with nothing saying so.
 *
 * ⚠️ **A `<p>` BESIDE THE HEADING RATHER THAN A `<span>` INSIDE IT.** It used to
 * sit inside the band's heading, which made a whole sentence part of the heading's
 * own text — so a screen reader announced *"Business School Festival - India, 3
 * companies, capacity 12, 4 cities, 2 companies are confirmed only for cities this
 * project no longer runs in"* as ONE heading. It is a note about the band, not the
 * band's name.
 */
function OffAxisNote({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <p className="m-0 mt-1 text-2xs text-fg-muted">
      {count} {count === 1 ? 'company is' : 'companies are'} confirmed only for cities this project
      no longer runs in
    </p>
  );
}

/* -------------------------------------------------------------------------
 * The table
 * --------------------------------------------------------------------- */

/**
 * The band's own heading, OUTSIDE its table.
 *
 * ✅ **OUTSIDE THE HORIZONTAL SCROLLER, WHICH RETIRES A HACK.** *SUPERSEDED: a
 * `colSpan` heading row inside the table whose text carried `sticky left-3`,
 * because "a spanning cell has no edge to freeze to — freezing it would pin the
 * entire band heading over every column".* A heading that is not inside the
 * scroller cannot scroll sideways out of view at all, which is what that
 * approximated.
 *
 * ⚠️ **ONE `<h3>` PER BAND, SHARED IN SHAPE WITH THE CARD VIEW.** The table used
 * to label its bands with a `<th scope="row" colSpan>` — a row header spanning
 * the row, which announces as a cell rather than as a section — while the card
 * view already used a heading. Both are headings now, so the two views read the
 * same way to a screen reader, and the table below keeps its own `<caption>`
 * naming the project for anybody who enters the grid directly.
 *
 * ⚠️ **THE TINT IS ON THIS BLOCK AND THE ROWS STAY PLAIN.** Not restraint for its
 * own sake: `Table`'s frozen body cell bakes in an opaque `bg-surface` because a
 * sticky cell must be opaque, and a striped tint appended through `className`
 * would be a second `background-color` on the same element — whichever Tailwind
 * emitted last wins. Off the table, the tint has nothing to race.
 *
 * ⛔ **NOT `sticky top-0` — see this file's header.** The window scrolls, the top
 * bar is `z-30` chrome, and the offset that would clear it varies with the
 * banners stacked inside that bar.
 */
function BandHeading({ project, index }: { project: EventCapacityProject; index: number }) {
  return (
    <div className={cx('border-b border-border px-4 py-2.5', bandTone(index))}>
      <h3 className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="min-w-0 truncate text-sm font-semibold text-fg">{project.name}</span>
        {bandFacts(project).map((fact) => (
          <span
            key={fact.key}
            className={cx(
              'inline-flex shrink-0 items-baseline gap-1.5 text-2xs font-normal tabular',
              fact.pending ? 'text-warning' : 'text-fg-muted',
            )}
          >
            {fact.text}
            {fact.badge && <Badge tone={fact.badge.tone}>{fact.badge.label}</Badge>}
          </span>
        ))}
      </h3>
      {/* Beside the heading, not inside it — see `OffAxisNote`. */}
      <OffAxisNote count={project.companiesOffAxis} />
    </div>
  );
}

/**
 * One band's header row — **its own project's cities, and nothing else.**
 *
 * 🔴 **THE COUNT UNDER EACH CITY IS THIS PROJECT'S OWN**
 * (`project.cities[].companyCount`), owner's decision 2026-09-13. ⚠️ *SUPERSEDED:
 * "**THE COUNT UNDER EACH CITY IS DISTINCT COMPANIES ACROSS THE GRID** — owner's
 * ruling. It is `cities[].companyCount` verbatim … ⛔ **No footer, no column
 * total, and no sentence about why it is smaller than the band figures add up
 * to**."* **Every number now describes the band it sits in**, so there is no
 * second scale on screen for a reader to try to reconcile — and the figure here
 * is the number of ticks in the column beneath it, which the global one never
 * was.
 *
 * ⛔ **A `0` IS A COUNTED ZERO**: this project runs in this city and nobody is
 * confirmed for it yet. The old not-applicable blank it had to be told apart from
 * is **unreachable here** — a city the project does not run in has no column.
 * It is set in `text-fg-subtle` so a reader scanning for activity is not drawn to
 * it, and it is still printed: a zero is a measurement.
 *
 * ⚠️ **EXACTLY ONE CELL PER BAND CITY AND NOTHING ELSE** — no decorative spacer
 * `<th>`. Nothing measures this row today, because the company column is the ONLY
 * frozen column and a single frozen column sits at offset 0 with no arithmetic at
 * all. **The moment a second column is frozen that stops being true**, and it
 * stops being true PER BAND: an offset helper indexes positionally against the
 * visible cells, so one extra cell shifts every offset by a column and the only
 * symptom is two frozen columns overlapping.
 *
 * ⚠️ **`wrap` on the city headings.** A long city name that cannot wrap runs past
 * its column, and this repo has had one heading painted over its neighbour
 * legibly enough that the row read as one long word rather than as a fault.
 *
 * ⚠️ **The figure is in its own `<span>`, not appended to the heading text.** A
 * column header is `text-2xs` uppercase with letter-spacing; a number set in that
 * is a number somebody has to squint at, and this one is the whole reason the
 * column exists.
 */
function BandTableHead({
  columns,
  grouped,
}: {
  columns: readonly EventCapacityProjectCity[];
  grouped: boolean;
}) {
  return (
    <THead>
      <TR>
        <TH frozen="left" className={COMPANY_W}>
          Company
        </TH>
        {grouped && <TH className={TYPE_W}>Type</TH>}
        {columns.map((city) => (
          <TH key={city.id} wrap className={cx(CITY_W, 'text-center')} title={cityLabelOf(city)}>
            {city.name}
            <span
              className={cx(
                'mt-0.5 block text-xs font-semibold normal-case tracking-normal tabular',
                city.companyCount === 0 ? 'text-fg-subtle' : 'text-fg',
              )}
              title={`${city.companyCount} ${
                city.companyCount === 1 ? 'company' : 'companies'
              } confirmed for ${cityLabelOf(city)} on this project`}
            >
              {city.companyCount}
              <span className="sr-only">
                {' '}
                {city.companyCount === 1 ? 'company' : 'companies'} confirmed for{' '}
                {cityLabelOf(city)} on this project
              </span>
              <PendingCityCount city={city} />
            </span>
          </TH>
        ))}
      </TR>
    </THead>
  );
}

/**
 * A company-group run's label: the SOLD rows, then the pending ones apart (owner
 * 2026-10-27) — a pending row never adds to the group's count. Shared by the
 * table and the cards so the two cannot count differently.
 */
function RunLabel({ label, rows }: { label: string; rows: readonly EventCapacityCompany[] }) {
  const sold = soldRowCount(rows);
  const pending = rows.length - sold;
  return (
    <>
      {label} · {sold}
      {pending > 0 && <span className="text-warning"> · {pending} pending</span>}
    </>
  );
}

/**
 * The column's PENDING figure (owner 2026-10-27), beside — never added to — the
 * sold count, so both stay countable against the ticks beneath them. Absent at 0.
 */
function PendingCityCount({ city }: { city: EventCapacityProjectCity }) {
  const count = city.pendingCount ?? 0;
  if (count <= 0) return null;
  const label = `${count} pending for ${cityLabelOf(city)} on this project`;
  return (
    <span className="ml-1 font-normal text-warning" title={label}>
      +{count}
      <span className="sr-only"> {label}</span>
    </span>
  );
}

/**
 * One company's row.
 *
 * ⛔ **THE TICK IS LOOKED UP BY `city.id`.** `columns` holds city objects rather
 * than an index into anything, and `ticks` is keyed by `cityId`, so there is no
 * position in this function for the two to disagree about. A blank cell means
 * this company is not confirmed for that city **on this project** — the only
 * blank left, now that a city the project does not run in has no column.
 */
function CompanyRow({
  company,
  columns,
  grouped,
}: {
  company: EventCapacityCompany;
  columns: readonly EventCapacityProjectCity[];
  grouped: boolean;
}) {
  const ticks = ticksByCityId(company);
  const pending = isPending(company);
  /*
   * ⚠️ THE PENDING TINT (owner 2026-10-27) IS NOT A SECOND `bg-` ON THE FROZEN
   * CELL — `Table` bakes an opaque `bg-surface` into it and two background
   * utilities race the stylesheet order. An absolutely placed wash inside the
   * (sticky, so positioned) cell paints over it instead, stopping 1px short of
   * the right and bottom edges so the cell's inset hairlines stay visible.
   */
  const tint = pending ? 'bg-warning-soft' : undefined;
  return (
    <TR>
      <TH scope="row" frozen="left" className={COMPANY_MAX_W}>
        {pending && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute top-0 right-px bottom-px left-0 bg-warning-soft"
          />
        )}
        <span className={cx('block min-w-0', pending && 'relative')}>
          <CompanyNameCell company={company} />
        </span>
      </TH>
      {grouped && (
        <TD className={cx(TYPE_MAX_W, 'truncate text-xs text-fg-secondary', tint)}>
          {companyTypeLabel(company)}
        </TD>
      )}
      {columns.map((city) => {
        const tick = ticks.get(city.id);
        return (
          <TD key={city.id} className={cx('text-center', tint)}>
            {tick === undefined ? null : <TickCell tick={tick} cityLabel={cityLabelOf(city)} />}
          </TD>
        );
      })}
    </TR>
  );
}

/**
 * One band — **its own heading and its own `<table>`.**
 *
 * 🔴 **A TABLE PER BAND IS WHAT LETS EACH BAND CARRY ITS OWN CITIES**, and it is
 * not a stylistic preference: one `<table>`'s columns are shared by every
 * `<tbody>` in it, so a three-city band inside a nine-column table must trail six
 * empty cells or span them — the global axis again under another name. Each band
 * therefore gets its own header, its own floor (`tableMinWidthPx` over ITS column
 * count) and its own horizontal scroller, so a band running four cities does not
 * inherit a scroll distance from the widest band on the page.
 *
 * ⚠️ **A BAND WITH NO COMPANIES IS NOT DROPPED.** It is the band somebody opens
 * this grid to find: a project that is running and that nobody has been confirmed
 * for. It keeps its heading, its header row and a row saying so.
 *
 * ✅ **A PROJECT WITH NO CITIES IS A BAND WITH ONE "Virtual" COLUMN** (owner
 * 2026-10-18). *SUPERSEDED: "The server excludes a project declaring no
 * cities."* The server sends that column as an ordinary entry in
 * `project.cities` and keys the ticks to its id, so nothing here special-cases
 * it beyond `bandFacts` not counting it as a city.
 *
 * ⚠️ **A BAND WITH NO COLUMNS AT ALL RENDERS A SENTENCE, NOT AN EMPTY TABLE.**
 * That is a contract violation rather than an ordinary state — but a one-column table with a header reading `Company`
 * and nothing beside it reads as a rendering fault, where a sentence says what
 * happened. It is NOT dropped: a band that vanished would leave the reader with
 * nothing to notice.
 *
 * ⚠️ **`isolate` ON THE SCROLLER.** There is now one set of `z-20`/`z-10` frozen
 * cells PER BAND rather than one per page, all of them sticky in the page's own
 * root stacking context. A context per band keeps each band's numbers ordering
 * against each other and against nothing else — and it is what makes a sticky
 * band heading, if the offset problem in this file's header is ever solved, a
 * local change rather than a move on the ladder.
 */
function Band({
  project,
  index,
  grouped,
}: {
  project: EventCapacityProject;
  index: number;
  grouped: boolean;
}) {
  const columns = bandColumns(project);
  const span = bodyColumnCount(columns.length, grouped);
  return (
    <section className="border-t border-border first:border-t-0">
      <BandHeading project={project} index={index} />

      {columns.length === 0 ? (
        <p className="m-0 px-4 py-3 text-xs text-fg-subtle">
          This project records no cities, so there is nothing to lay out against.
        </p>
      ) : (
        <TableWrap className="isolate" frozenHeader frozenMaxHeight="min(60vh, 560px)">
          {/* See `tableMinWidthPx` — the sum is DATA and cannot be a class. */}
          <Table style={{ minWidth: `${tableMinWidthPx(columns.length, grouped)}px` }}>
            <caption className="sr-only">
              Companies confirmed for each city on {project.name}
            </caption>
            <BandTableHead columns={columns} grouped={grouped} />
            <TBody>
              {project.companies.length === 0 ? (
                <TR>
                  {/* ⚠️ DERIVED — see `bodyColumnCount`. Never a constant. */}
                  <TD colSpan={span} className="text-xs text-fg-subtle">
                    No company is confirmed for this project yet.
                  </TD>
                </TR>
              ) : grouped ? (
                /*
                 * Institutions first, then Partners (owner 2026-10-18). The server
                 * orders the rows; this only finds the run boundaries. The label
                 * sits in the frozen cell so it stays on screen while scrolling.
                 */
                splitByCompanyGroup(project.companies, (c) => c.group).map((run) => (
                  <Fragment key={run.key}>
                    <TR>
                      <TD frozen="left" className="py-1.5 text-2xs font-medium text-fg-muted">
                        <RunLabel label={run.label} rows={run.rows} />
                      </TD>
                      <TD colSpan={span - 1} className="py-1.5" />
                    </TR>
                    {run.rows.map((company) => (
                      <CompanyRow
                        key={company.id}
                        company={company}
                        columns={columns}
                        grouped={grouped}
                      />
                    ))}
                  </Fragment>
                ))
              ) : (
                project.companies.map((company) => (
                  <CompanyRow key={company.id} company={company} columns={columns} grouped={false} />
                ))
              )}
            </TBody>
          </Table>
        </TableWrap>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------
 * The skeleton
 * --------------------------------------------------------------------- */

/**
 * 🔴 **A SKELETON PER BAND, BECAUSE A BAND IS NOW A WHOLE TABLE.** *SUPERSEDED: a
 * single `<thead>` plus one `<tbody>` per band inside one table.* A skeleton whose
 * structure does not match what replaces it reads as the page jumping when the
 * data lands, and the structural change here is the largest one available — the
 * loaded view is N tables, so the skeleton is N tables.
 *
 * ⚠️ **A HEADER IS DRAWN PER BAND, WHICH IS THE SHAPE THE READER IS ABOUT TO
 * MEET.** Under the old global axis there was one header at the top of the page;
 * drawing that here would promise a layout this panel no longer has.
 *
 * ⚠️ **NO REAL HEADER COMPONENT FED FAKE CITIES.** Manufacturing five nameless
 * city objects to reuse `BandTableHead` puts invented data — negative ids, empty
 * names, an empty `title`, a `companyCount` of nothing — into the one component
 * whose job is to render what the server sent. The two agree because the widths
 * and the cell count are shared constants, which is the part that has to agree.
 * **`Company` is real text in both**, because it is the one heading that does not
 * depend on the response.
 *
 * ⚠️ **IT CARRIES NO BAND TINT AND GUESSES NO COUNTS.** How many bands there are,
 * how many cities each runs in and what each tallies are exactly the things the
 * response has not said yet, so a skeleton showing any of them would be drawing a
 * shape the data is about to contradict.
 */
const SKELETON_BANDS = 2;
const SKELETON_ROWS = 4;
const SKELETON_CITIES = 5;

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: SKELETON_BANDS }, (_, bandIndex) => (
        <section key={bandIndex} className="border-t border-border first:border-t-0">
          <div className="border-b border-border px-4 py-2.5">
            <Skeleton className="h-3.5 w-56" />
          </div>
          <TableWrap className="isolate" frozenHeader frozenMaxHeight="min(60vh, 560px)">
            {/* The same floor arithmetic the loaded band uses — see `tableMinWidthPx`. */}
            <Table style={{ minWidth: `${tableMinWidthPx(SKELETON_CITIES, false)}px` }}>
              <THead>
                <TR>
                  <TH frozen="left" className={COMPANY_W}>
                    Company
                  </TH>
                  {/*
                    ⚠️ TWO lines, because the loaded header is two: a city name
                    and its count. One line here and the header row changes
                    height when the data lands.
                  */}
                  {Array.from({ length: SKELETON_CITIES }, (_, cityIndex) => (
                    <TH key={cityIndex} className={cx(CITY_W, 'text-center')}>
                      <Skeleton className="mx-auto h-2.5 w-12" />
                      <Skeleton className="mx-auto mt-1 h-3 w-4" />
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                  <TR key={rowIndex}>
                    <TD frozen="left" className={COMPANY_MAX_W}>
                      <Skeleton className="h-3 w-40" />
                    </TD>
                    {Array.from({ length: SKELETON_CITIES }, (_, cityIndex) => (
                      <TD key={cityIndex} className="text-center">
                        <Skeleton className="mx-auto h-3 w-3" />
                      </TD>
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </section>
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------
 * The card fallback
 * --------------------------------------------------------------------- */

/**
 * The same data as a stacked list.
 *
 * ⚠️ **A cross-tab cannot be a table at 320px**, so below `lg` a band becomes a
 * section and a company a card listing the cities it is confirmed for.
 *
 * ⛔ **IT IS NOT A REDUCED SET.** Every band, every company, every tick, the
 * all-cities marker, the per-city tally, the capacity and the off-axis line are
 * all here — a feature reachable only above a breakpoint is indistinguishable
 * from one that was removed, and for a good many people the phone is the only
 * screen this is ever read on.
 *
 * 🔴 **THE `Companies by city` ROSTER IS DELETED, not moved.** *SUPERSEDED: a
 * `CityAxisCards` section above the bands carrying `data.cities[].companyCount`,
 * "so the distinct-company count would not be reachable only above a
 * breakpoint".* Owner's decision 2026-09-13: **the global figure goes**, so there
 * is no longer a table header for it to be the phone's counterpart of. ⚠️ **The
 * reasoning that put it here has not changed and still binds** — anything the
 * table shows must be here too — which is exactly why the tally below, the card
 * view's version of the band's own header counts, stays.
 *
 * ✅ **THE HEADING IS THE SAME COMPONENT THE TABLE USES.** `BandHeading` renders
 * in both views, so the tone, the facts, the capacity and the off-axis line
 * cannot answer differently across the breakpoint — a reader crossing it must not
 * have to relearn which band is which. *SUPERSEDED: a second `<h3>` written out
 * here with its own smaller type and its own gap scale.*
 */
function BandCards({
  project,
  index,
  grouped,
}: {
  project: EventCapacityProject;
  index: number;
  grouped: boolean;
}) {
  const columns = bandColumns(project);

  return (
    <section className="border-t border-border first:border-t-0">
      <BandHeading project={project} index={index} />

      {/*
        The band's own per-city counts — the card view's version of the column
        header, and the only place below `lg` those figures exist.

        ✅ **THE `?? 0` HAZARD IS GONE STRUCTURALLY.** *SUPERSEDED: a
        `counts.get(city.id)` lookup with a comment explaining at length why no
        `?? 0` was written beside it even though the lookup could not miss.* The
        count is a field ON the city object this list is built from, so there is
        no lookup, no miss to handle, and no fallback for the next reader to
        mistake for a safe one. A city the project does not run in is absent from
        `columns` entirely rather than printed as a zero.
      */}
      {columns.length === 0 ? (
        <p className="m-0 px-4 py-3 text-xs text-fg-subtle">
          This project records no cities, so there is nothing to lay out against.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 border-b border-border px-4 py-2">
          {columns.map((city) => (
            <li key={city.id} className="text-2xs text-fg-muted" title={cityLabelOf(city)}>
              <span className="text-fg-secondary">{city.name}</span>{' '}
              <span
                className={cx('tabular', city.companyCount === 0 ? 'text-fg-subtle' : 'text-fg')}
              >
                {city.companyCount}
              </span>
              <PendingCityCount city={city} />
            </li>
          ))}
        </ul>
      )}

      {project.companies.length === 0 ? (
        <p className="m-0 px-4 py-3 text-xs text-fg-subtle">
          No company is confirmed for this project yet.
        </p>
      ) : grouped ? (
        splitByCompanyGroup(project.companies, (c) => c.group).map((run) => (
          <Fragment key={run.key}>
            <h4 className="m-0 border-b border-border px-4 py-1.5 text-2xs font-medium text-fg-muted">
              <RunLabel label={run.label} rows={run.rows} />
            </h4>
            {run.rows.map((company) => (
              <CompanyCard key={company.id} company={company} columns={columns} grouped />
            ))}
          </Fragment>
        ))
      ) : (
        project.companies.map((company) => (
          <CompanyCard key={company.id} company={company} columns={columns} grouped={false} />
        ))
      )}
    </section>
  );
}

/** One company as a card, below `lg`. */
function CompanyCard({
  company,
  columns,
  grouped,
}: {
  company: EventCapacityCompany;
  columns: readonly EventCapacityProjectCity[];
  grouped: boolean;
}) {
  const ticks = ticksByCityId(company);
  /*
   * ⛔ **BY ID.** `columns` is the band's own cities and `ticks` is keyed
   * by `cityId`, so a company confirmed for one city on a four-city band
   * keeps its one chip against the right city. A positional pairing here
   * would chip the FIRST city of the band for every company that has any
   * confirmation at all — plausible on a one-city band, wrong on the next.
   */
  const confirmed = columns.filter((city) => ticks.has(city.id));
  const pending = isPending(company);
  return (
    <article
      className={cx('border-b border-border px-4 py-3 last:border-b-0', pending && 'bg-warning-soft')}
    >
      <CompanyNameCell company={company} />
      {grouped && (
        <span className="mt-0.5 block text-2xs text-fg-secondary">
          <span className="text-fg-muted">Type</span> {companyTypeLabel(company)}
        </span>
      )}
      {confirmed.length === 0 ? (
        <p className="mt-2 mb-0 text-xs text-fg-subtle">
          Not confirmed for any city on this project.
        </p>
      ) : (
        <ul className="mt-2 mb-0 flex list-none flex-wrap gap-1.5 p-0">
          {confirmed.map((city) => {
            const tick = ticks.get(city.id);
            if (!tick) return null;
            return (
              <li
                key={city.id}
                className={cx(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-2xs',
                  tick.pending === true
                    ? 'border-warning-border text-warning'
                    : 'border-border text-fg-secondary',
                )}
                title={tickTitle(tick, cityLabelOf(city))}
              >
                {city.name}
                {tick.viaAllCities && (
                  <span
                    aria-hidden="true"
                    className={tick.pending === true ? 'text-warning' : 'text-fg-muted'}
                  >
                    *
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

/* -------------------------------------------------------------------------
 * The legend
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **NOT HELPER TEXT — the fourth exception.** It states a fact about the data
 * that nothing else on screen can: that some ticks come from a line naming every
 * city rather than from a line naming this one. Without it the marker is a
 * decoration nobody can resolve. A helper-text sweep must not delete it.
 */
function CapacityLegend() {
  /*
   * The PENDING swatch (owner 2026-10-27) is the same kind of fact: a yellow
   * tick is a deal line, not a sale, and nothing else on screen says so.
   */
  return (
    <div className="m-0 flex flex-col gap-1 text-2xs text-fg-muted">
      <p className="m-0">
        <span aria-hidden="true">*</span> Confirmed through a line covering every city on the
        project, rather than a line naming that city.
      </p>
      <p className="m-0 inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="inline-flex items-center rounded-sm border border-warning-border bg-warning-soft px-1 text-warning"
        >
          <CheckMark />
        </span>
        Pending: on a deal, not yet sold, and never counted as sold.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * The tab toggle
 * --------------------------------------------------------------------- */

export type DeliverablesTab = 'tracker' | 'capacity';

/**
 * Reads the tab out of a query string.
 *
 * ⚠️ **THE DEFAULT IS SPELLED BY THE KEY'S ABSENCE**, like `?tab=` one level up,
 * so `/client-deliverables` and `/client-deliverables?deliverables=tracker` are
 * one URL for one screen rather than two for it. Anything unrecognised reads as
 * the default too — an unknown tab is not a reason to show nothing.
 */
export function deliverablesTabOf(params: URLSearchParams): DeliverablesTab {
  return params.get(DELIVERABLES_TAB_PARAM) === CAPACITY_TAB_VALUE ? 'capacity' : 'tracker';
}

/*
 * ⚠️ **THE LABEL MOVED AND THE URL VALUE DID NOT — owner's rename 2026-09-13,
 * *"Event Capacity"* → *"Project Capacity"*.** `?deliverables=capacity` is
 * unchanged, and must stay unchanged: every existing bookmark and shared link
 * names it, and a renamed value would fall through `deliverablesTabOf` to the
 * default tab **with nothing failing**. Same ruling as the permission sets, the
 * role labels and `trackers-invoice` — write ids, render names, match on
 * neither. The component, its props and its types keep their names too.
 *
 * ⚠️ **TITLE CASE HERE, SENTENCE CASE EVERYWHERE ELSE**, deliberately: this row
 * sits beside `Deliverables` in one control, while the panel header and the
 * empty states are prose. Do not harmonise the two.
 */
const TABS: readonly { value: DeliverablesTab; label: string }[] = [
  { value: 'tracker', label: 'Deliverables' },
  { value: 'capacity', label: 'Project Capacity' },
];

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`.** These switch what
 * the page below IS rather than labelling panels that share one tabstop, which
 * is how every other page-level view switch in this app is built. A real
 * tablist would promise arrow-key navigation between panels that do not share a
 * tabstop.
 *
 * ⚠️ **BOTH PILLS RENDER AT EVERY WIDTH.** This codebase has already had a
 * working feature reported as *deleted* when it had merely become unreachable
 * behind a toggle. The selected pill is not distinguished by colour alone:
 * `secondary` gives it a raised surface against `ghost`'s transparent one, and
 * `aria-pressed` states it outright.
 *
 * ⚠️ **NOTHING IS WRITTEN ON ARRIVAL.** Choosing the tracker DELETES the key
 * rather than writing `tracker`, because the default is the absence. An arrival
 * write would also race the tracker's own saved-filter seeding, which builds a
 * whole query string from its first render's snapshot.
 */
export function DeliverablesTabToggle() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = deliverablesTabOf(searchParams);

  const change = (next: DeliverablesTab) => {
    if (next === active) return;
    /*
     * ⚠️ ONE `URLSearchParams`, every write, one `setSearchParams`. Two setters
     * in one gesture both start from the render's snapshot, so whichever lands
     * last is the only one that survives — and nothing errors.
     *
     * ⚠️ **THE OTHER TAB'S FILTERS ARE LEFT ALONE, deliberately.** The two tabs
     * own disjoint keys (see `CAPACITY_CYCLE_PARAM`), so nothing carried across
     * can narrow the screen it is not meant for; and clearing them would throw
     * away a reader's filters for flipping over to look at something.
     */
    const params = new URLSearchParams(searchParams);
    if (next === 'tracker') params.delete(DELIVERABLES_TAB_PARAM);
    else params.set(DELIVERABLES_TAB_PARAM, CAPACITY_TAB_VALUE);
    // Switching a tab adjusts what is on screen rather than navigating, so it
    // must not put a Back-button step between the reader and where they came
    // from.
    setSearchParams(params, { replace: true });
  };

  return (
    <ViewToggle
      label="Client Deliverables view"
      value={active}
      options={[...TABS]}
      onChange={change}
    />
  );
}

/* -------------------------------------------------------------------------
 * The screen
 * --------------------------------------------------------------------- */

/**
 * Every member of the CSV filter, validated.
 *
 * ⚠️ **A BAD MEMBER IS DROPPED HERE AND NEVER SENT, AND THE CONTROL AGREES.**
 * The chips are rendered from this same list, so a member that was thrown away
 * is absent from the chips too — the filter cannot show a tick for something the
 * request did not carry. That agreement is the whole reason dropping is safe.
 */
function readIdList(raw: string): number[] {
  if (raw.trim() === '') return [];
  const ids: number[] = [];
  for (const part of raw.split(',')) {
    const value = Number(part.trim());
    /*
     * ⚠️ A bad member is DROPPED here rather than sent. The server refuses a
     * malformed CSV outright — `?projectId=1,abc` is a 422 and never a silent
     * narrow to `[1]` — and a request that can only ever be refused is not worth
     * making. What must never happen is sending the FIRST of several: that is a
     * `200`, a list narrowed to one project, and a filter drawn with two ticks.
     */
    if (Number.isInteger(value) && value > 0) ids.push(value);
  }
  return ids;
}

export interface EventCapacityPanelProps {
  /**
   * How to read the grid.
   *
   * ✅ **PASSED — `ClientDeliverablesPage` mounts this with `api.eventCapacity`**,
   * so the not-reading notice below is unreachable. *SUPERSEDED: "OMITTED TODAY
   * … `api.eventCapacity` does not exist yet."*
   *
   * ⚠️ **STILL OPTIONAL, AND THE NOTICE STAYS.** Being structural is what made
   * the wiring one line; a boolean would have had to be found and unset. See this
   * file's header.
   */
  load?: EventCapacityLoader;
  /**
   * The export control's slot.
   *
   * ⚠️ **THE EXPORT IS SERVER-SIDE AND IS NOT THIS FILE'S.** Another session is
   * building an xlsx endpoint behind the existing binary-download pattern. This
   * renders NOTHING today, deliberately: a button whose only outcome is a failed
   * request is an affordance this app forbids, and there is no client-side
   * spreadsheet here to reach for instead.
   */
  exportAction?: ReactNode;
}

export function EventCapacityPanel({ load, exportAction }: EventCapacityPanelProps = {}) {
  const { ability } = useAuth();
  /*
   * The read gate on the SURFACE, not on the records — the same module the
   * tracker beside it answers to, because this is the same screen with a second
   * tab rather than a page of its own.
   *
   * ⚠️ It DEGRADES OPEN: only an explicit `none` scope denies, so an unseeded
   * catalogue, an older API or a failed `/permissions/me` all leave it visible.
   * ⚠️ **Hiding a page is not refusing a request** — nothing here may be worded
   * as though the data were protected.
   *
   * Computed here rather than as an early return, because the return has to sit
   * below every hook.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.trackerClientDeliverables);

  /*
   * 🔴 **COMPUTED BEFORE THE HOOKS THAT DEPEND ON IT, because it gates two
   * REQUESTS.** With no loader there is nothing to filter, so the cycle and
   * project masters are not read at all — two requests spent on controls that
   * could not narrow anything. ⚠️ *SUPERSEDED, the weaker half: "against a
   * 120-per-60s limit shared by the whole office".* It is **12,000/60s**
   * (corrected 2026-09-17), so two requests breach no budget; what survives is
   * that they buy a reader nothing, which stands on its own.
   * It is also why the filter bank itself is not rendered in that
   * state: a control whose only outcome is nothing happening is an affordance
   * this app forbids.
   */
  const notWired = load === undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);

  const cycleParam = params.get(CAPACITY_CYCLE_PARAM) ?? '';
  const projectParam = params.get(CAPACITY_PROJECT_PARAM) ?? '';
  const projectIds = useMemo(() => readIdList(projectParam), [projectParam]);

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      // A default is spelled by the key's ABSENCE — one canonical URL per view.
      if (value === '') next.delete(key);
      else next.set(key, value);
      // Changing a filter is not a navigation; a history entry per change would
      // make Back mean "undo one filter".
      setSearchParams(next, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * ⚠️ Gated on BOTH the permission gate and the loader. A reader who cannot open
   * this screen must not spend the office's request budget on its pickers, and
   * neither must a screen with nothing to read.
   */
  const optionsEnabled = canReadSurface && !notWired;
  const cycles = useCycleOptions(optionsEnabled);
  const projects = useProjectOptions(optionsEnabled);

  /*
   * The cycle the URL NAMES, or `null` when it names nothing readable.
   *
   * ⚠️ **An unreadable `capacityCycleId` reads as NAMING NOTHING, not as an
   * error** — the same treatment `readIdList` gives a bad CSV member, and for
   * the same reason: the control is rendered from this value too, so the picker
   * cannot show a cycle the request did not carry.
   */
  const namedCycleId = useMemo(() => {
    if (cycleParam === '') return null;
    const value = Number(cycleParam);
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [cycleParam]);

  /*
   * ⛔ **NOTHING IS WRITTEN TO THE URL ON ARRIVAL.** The default cycle is
   * RESOLVED on the read and never seeded into the query string: an arrival
   * write here would race the tracker's saved-filter seeding on the same
   * document, and one of the two would lose silently.
   *
   * ⚠️ `currentCycle` matches on the DATE WINDOW, not on the newest row — live
   * cycles do not arrive in date order, and the highest id in production is the
   * EARLIEST financial year in the table. ⛔ **It is this app's ONE definition of
   * "the cycle running today"** — it also names a new deal or quote and decides
   * what `/projects` opens on. Do not write a second one here, do not hardcode a
   * name or an id, and never take `MAX(id)`.
   *
   * 🔴 **`null` IS AN ORDINARY ANSWER AND IT IS NOT A REQUEST.** *SUPERSEDED: "It
   * sends no `cycleId` and lets the server answer for whatever it considers
   * current."* **`cycleId` is required — omitting it is a 422 naming it**, so a
   * missing cycle becomes `noCycle` below and the reader is asked, rather than a
   * request being fired that could only be refused.
   */
  const resolvedCycleId = useMemo(
    () => namedCycleId ?? currentCycle(cycles.cycles)?.id ?? null,
    [namedCycleId, cycles.cycles],
  );

  /*
   * ⚠️ Held until the cycle master has settled, so the first request is not
   * fired without the default the reader is about to see. `cycles.failed` ends
   * the wait too — a request that never came back is not a reason to hang.
   */
  const cyclesSettled = !cycles.loading;

  /*
   * 🔴 **NOBODY HAS CATALOGUED A CYCLE COVERING TODAY — AN ORDINARY STATE, AND
   * NEITHER AN ERROR NOR AN EMPTY GRID.** Today falls between two business
   * years, the first has not been set up, or the master failed to load. The
   * honest answer is a prompt to choose one, beside a picker that can, and NO
   * REQUEST — see the empty-state chain and the effect.
   *
   * ⚠️ **Only once the master has SETTLED.** Before that `currentCycle` has an
   * empty list to match against and answers `null` for every cycle in the
   * system, so the screen would ask the reader to choose and then answer itself
   * a moment later.
   */
  const noCycle = cyclesSettled && resolvedCycleId === null;

  const [result, setResult] = useState<EventCapacityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const projectKey = projectIds.join(',');

  useEffect(() => {
    if (load === undefined) {
      /*
       * Not wired yet — see the header. No request, and no spinner that would
       * never resolve: a permanent loading state is the one failure mode that
       * looks like a slow server for ever.
       */
      setLoading(false);
      return;
    }
    if (!cyclesSettled) return;
    /*
     * ⛔ **NO REQUEST WITHOUT A CYCLE.** `cycleId` is REQUIRED — omitting it is a
     * 422 naming it, never a current-cycle grid — so a request here could only
     * ever be refused, and a refusal renders as the screen being broken rather
     * than as nobody having catalogued a cycle for today.
     *
     * ⚠️ **`result` AND `error` ARE CLEARED WITH IT.** Leaving the last grid in
     * state would leave the heading counting projects "in F.Y. 2026 - 27" above a
     * body asking which cycle to use — a screen labelled with a cycle its figures
     * are no longer about, which is the exact failure the label-from-the-echo rule
     * exists to prevent.
     *
     * ⚠️ **SPELLED AS THE NULL TEST RATHER THAN AS `noCycle`, DELIBERATELY.** Past
     * the settled guard above the two are the same condition, but only this form
     * NARROWS `resolvedCycleId` to `number` for the call below — which is what
     * makes the required `cycleId` unmissable at compile time instead of at the
     * server. A `noCycle` test here needs a `!` on the argument, and a `!` is a
     * promise nothing checks.
     */
    if (resolvedCycleId === null) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    /*
     * ⚠️ Re-derived from `projectKey` rather than closing over `projectIds`.
     * The array is a fresh identity on every render, so depending on it would
     * fire a request per render; the joined string is the value that actually
     * changes. `''` means the key is omitted — every project.
     */
    const ids = projectKey === '' ? [] : projectKey.split(',').map(Number);
    load({ cycleId: resolvedCycleId, projectIds: ids }, signal)
      .then((response) => {
        if (signal.aborted) return;
        setResult(response);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setError(caught instanceof Error ? caught.message : 'Failed to load project capacity');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [load, cyclesSettled, resolvedCycleId, projectKey]);

  /*
   * ⛔ **`result.data.cities` IS NOT READ.** It is the GLOBAL axis, and both of
   * its jobs went on 2026-09-13: the layout is per band (`bandColumns`) and the
   * distinct-companies roster it fed is deleted on the owner's decision. It is
   * still on the wire, so a reader looking for it will find it — **do not wire it
   * back in to save a `.some()` below.** Every figure on this screen has to
   * describe the band it sits in, and that one describes the grid.
   */
  const bands = useMemo(() => result?.data.projects ?? [], [result]);
  const grouped = useMemo(() => responseIsGrouped(bands), [bands]);
  /*
   * ⚠️ **`!notWired && !noCycle` IS LOAD-BEARING, not belt and braces.** `loading`
   * starts `true`, and the effect that clears it for a panel with nothing to
   * request runs AFTER the first paint — so without them the notice is preceded
   * by a flash of skeleton rows for a grid that is never going to arrive.
   *
   * ⚠️ **Folded in HERE rather than repeated at the two skeleton sites**, which is
   * where `!notWired` used to sit. A THIRD state with nothing to fetch would
   * otherwise have had to find both of them, and missing one is a flash nobody
   * reports — it looks like the page loading.
   */
  const initialLoad = loading && result === null && !notWired && !noCycle;

  const cycleOptions: ComboboxOption[] = useMemo(
    () => cycles.cycles.map((cycle) => ({ value: String(cycle.id), label: cycle.cycleName })),
    [cycles.cycles],
  );

  /*
   * ⚠️ **THE PICKER FOLLOWS THE RESPONSE ONCE ONE ARRIVES.** Before that it
   * shows whatever the URL names, or the locally-resolved current cycle. After
   * it, it shows the cycle the SERVER answered for — which is the only cycle the
   * figures on screen are about.
   *
   * ⚠️ **THAT ECHO CAN NO LONGER DISAGREE WITH `resolvedCycleId`, AND THE BRANCH
   * IS KEPT ANYWAY.** *SUPERSEDED: "the two can legitimately differ when no cycle
   * contains today."* They differed only while a missing cycle meant *omit the
   * key and let the server choose*; `cycleId` is required now, so the server
   * echoes back exactly what was sent and a missing cycle sends nothing at all.
   * The branch stays because the ECHO is still the authority for the heading —
   * reading the response rather than the picker is what stops this screen ever
   * labelling a grid with a cycle its figures are not about.
   *
   * ⛔ **NO `emptyOptionLabel`.** `data.cycle` is never null, so this endpoint
   * always answers for exactly one cycle: "All cycles" is not a state it can be
   * in, and offering it would be a filter that silently does nothing.
   *
   * ⚠️ **`namedCycleId`, NEVER THE RAW `cycleParam`.** A `Combobox` falls back to
   * its PLACEHOLDER for a value absent from its options, so a garbage
   * `?capacityCycleId=abc` passed through verbatim drew "Current cycle" while
   * the URL named nonsense — the control silently misreporting its own state.
   * The validated id is what the request carries, so it is what the control
   * shows.
   */
  const cycleValue =
    namedCycleId !== null
      ? String(namedCycleId)
      : result !== null
        ? String(result.data.cycle.id)
        : resolvedCycleId !== null
          ? String(resolvedCycleId)
          : '';

  const projectOptions: MultiComboboxOption[] = useMemo(
    () =>
      projects.projects.map((project) => ({
        value: String(project.id),
        label: project.name.trim() || `Project #${project.id}`,
      })),
    [projects.projects],
  );

  const cycleLabel = result?.data.cycle.name ?? '';
  const countLabel = initialLoad
    ? 'Loading…'
    : result === null
      ? ''
      : `${bands.length} ${bands.length === 1 ? 'project' : 'projects'}${
          cycleLabel === '' ? '' : ` in ${cycleLabel}`
        }`;

  /*
   * ⚠️ Below every hook, so an early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader eyebrow="Trackers" title="Client Deliverables" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  /*
   * The states, in the order they are decided. The two that are NOT failures
   * come first and must never render as one — the same shape `/integrations`
   * uses for an entity the backend has not wired yet.
   *
   * ⛔ **`noCycle` MUST OUTRANK THE THREE BELOW IT**, because every one of them
   * is a claim about a cycle: *"no projects in this cycle"* over a screen that
   * has no cycle is a statement about data nobody asked for.
   *
   * 🔴 **AND A FAILED READ RENDERS NO EMPTY STATE AT ALL.** A `cycleId` naming no
   * row is a **404, never an empty grid** — an empty grid legitimately means
   * *nothing is sold in this cycle yet*, so a mistyped id must not be able to
   * impersonate it. Without this guard the banner above and *"No projects in this
   * cycle"* below it render TOGETHER, and the second is a statement about the data
   * made on the strength of a request that never came back. The banner is the
   * answer; nothing goes under it.
   */
  const loadFailed = error !== null && result === null;

  const emptyState = loadFailed ? null : notWired ? (
    <EmptyState
      title="Project capacity isn’t reading yet"
      description="The grid is built and the endpoint it reads is not deployed. The Deliverables tab beside this one is unaffected."
    />
  ) : noCycle ? (
    /*
     * ⚠️ **THE PICKER IS ALREADY ON SCREEN — the filter bank above this body —
     * so this asks the reader to use a control they can see.** Do not add a
     * second cycle picker inside the empty state: two controls writing one query
     * key is how they come to disagree about what is selected.
     *
     * ⚠️ **A FAILED MASTER AND A GAP BETWEEN BUSINESS YEARS GET DIFFERENT
     * SENTENCES.** Both land here, and saying "no cycle covers today" on the
     * strength of a request that never came back is a claim about the data made
     * from a network failure. The Cycle field states the failure itself; this
     * says what it means for the screen.
     */
    <EmptyState
      title="Pick a cycle to see project capacity"
      description={
        cycles.failed
          ? 'The cycle list couldn’t be loaded, so there is no cycle for this grid to open on.'
          : 'No cycle covers today, so there is nothing to open on. Choose one above.'
      }
    />
  ) : projectIds.length > 0 && bands.length === 0 ? (
    <EmptyState
      title="No projects match that filter"
      description="Clear the project filter to see every project in this cycle."
      action={
        <Button size="sm" variant="secondary" onClick={() => setParam(CAPACITY_PROJECT_PARAM, '')}>
          Clear the project filter
        </Button>
      }
    />
  ) : bands.length === 0 ? (
    /* Title only — owner 2026-10-18 (ruling 4b). */
    <EmptyState title="No projects in this cycle" />
  ) : (
    <EmptyState title="None of these projects runs in a city yet" />
  );

  /*
   * ⚠️ **DERIVED FROM THE BANDS, NOT FROM THE GLOBAL AXIS.** *SUPERSEDED:
   * `bands.length > 0 && cities.length > 0`.* The two answer the same question
   * today — the axis is the union of the bands' cities — and only this form stays
   * true of what is actually rendered, which is `bandColumns` per band. An axis
   * that ever disagreed with its own members would decide here whether a grid
   * draws at all.
   */
  const hasGrid = bands.some((project) => project.cities.length > 0);

  return (
    <>
      <PageHeader
        eyebrow="Trackers"
        title="Client Deliverables"
        actions={
          <>
            <DeliverablesTabToggle />
            {/*
              The export slot. Nothing is rendered until an endpoint exists —
              see `EventCapacityPanelProps.exportAction`.
            */}
            {exportAction}
          </>
        }
      />

      <Panel>
        <PanelHeader title="Project capacity" description={countLabel} />

        {/*
          ⛔ **NO FILTER BANK WHILE THERE IS NOTHING TO FILTER.** With no loader
          these two controls would write query keys nothing reads — an affordance
          whose only outcome is nothing happening, which this app forbids — and
          their option masters are not fetched either. See `notWired`.
        */}
        {!notWired && (
        <div className="border-b border-border px-4 py-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Cycle"
              {...(cycles.failed ? { error: 'Couldn’t load cycle options.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={cycleValue}
                  onChange={(value) => setParam(CAPACITY_CYCLE_PARAM, value)}
                  options={cycleOptions}
                  placeholder="Current cycle"
                  loading={cycles.loading}
                  aria-describedby={describedBy}
                  {...(cycles.hasMore
                    ? { footnote: 'Showing the first 100 cycles.' }
                    : {})}
                />
              )}
            </Field>

            <Field
              label="Projects"
              {...(projects.failed ? { error: 'Couldn’t load project options.' } : {})}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={projectIds.map(String)}
                  onChange={(values) => setParam(CAPACITY_PROJECT_PARAM, values.join(','))}
                  options={projectOptions}
                  placeholder="Every project"
                  loading={projects.loading}
                  summaryNoun={{ singular: 'project', plural: 'projects' }}
                  aria-describedby={describedBy}
                  {...(projects.hasMore
                    ? {
                        /*
                         * ⚠️ **A FACT ABOUT THE DATA, NOT ADVICE.** `/projects`
                         * caps `perPage` at 100 — the server's own ceiling — and
                         * production holds more active projects than that, so
                         * the tail of the alphabet is genuinely unreachable
                         * here. Without this line a project that is simply
                         * missing from the list is indistinguishable from one
                         * that does not exist.
                         */
                        footnote: 'Showing the first 100 projects.',
                      }
                    : {})}
                />
              )}
            </Field>
          </div>
        </div>
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            title="Couldn’t load project capacity."
            className="mx-4 my-3"
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result !== null && 'opacity-60',
          )}
        >
          {/* Below lg a cross-tab cannot be a table at 320px — see `BandCards`. */}
          <div className="lg:hidden">
            {initialLoad ? (
              <div className="grid gap-3 p-4">
                {Array.from({ length: 3 }, (_, index) => (
                  <Skeleton key={index} className="h-24 w-full" />
                ))}
              </div>
            ) : !hasGrid ? (
              emptyState
            ) : (
              <>
                {/*
                  ⛔ **NO `Companies by city` ROSTER ABOVE THE BANDS.** Owner's
                  decision 2026-09-13 — see `BandCards`. Every figure here belongs
                  to the band it sits in.
                */}
                {bands.map((project, index) => (
                  <BandCards key={project.id} project={project} index={index} grouped={grouped} />
                ))}
                <div className="border-t border-border px-4 py-3">
                  <CapacityLegend />
                </div>
              </>
            )}
          </div>

          <div className="hidden lg:block">
            {initialLoad ? (
              /*
               * ⚠️ The skeleton owns its own tables now — one per band, each with
               * its own floor — because that is the shape it is standing in for.
               * See `TableSkeleton`.
               */
              <TableSkeleton />
            ) : !hasGrid ? (
              emptyState
            ) : (
              <>
                {/*
                  ⚠️ **NO WRAPPING `<caption>` ANY MORE, AND NOTHING LOST.** There
                  are N tables here, each captioned with its own project; the CYCLE
                  the whole set is about is already said twice — visibly in
                  `PanelHeader`'s description and in the `role="status"` line above
                  — so a third statement of it would only be a heading level
                  invented to hold it.
                */}
                {bands.map((project, index) => (
                  <Band key={project.id} project={project} index={index} grouped={grouped} />
                ))}
                <div className="border-t border-border px-4 py-3">
                  <CapacityLegend />
                </div>
              </>
            )}
          </div>
        </div>
      </Panel>
    </>
  );
}
